import './styles.scss';
import React, { useEffect, useState } from 'react';
import { ReactComponent as ArrowDownIcon } from './icons/arrow-down.svg';
import { ReactComponent as RemoveIcon } from './icons/close.svg';
import { Dropdown, DropdownItem, DropdownList, Input } from '@cokitchen/cokitchen-components';
import { OptionListProps, SelectItem, SelectProps, SelectedValuesProps } from './types';

export default function Select(props: SelectProps) {
  const {
    options,
    loading,
    multiple,
    hasSearch,
    placeholder,
    dropdownPlaceholder,
    small,
    disabled,
    onChange,
    className,
    onSearch
  } = props;

  const [showDropdown, setShowDropdown] = useState(false);
  const [isAllSelected, setAllSelected] = useState(false);
  const [selectedValueObjs, setSelectedValueObj] = useState<SelectItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<SelectItem[]>(options);

  useEffect(() => {
    if (searchValue) {
      setFilteredOptions(
        options.filter((o) => o.text.toLowerCase().includes(searchValue.toLowerCase()))
      );
    } else setFilteredOptions(options);
  }, [options, searchValue]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!filteredOptions.length) onSearch?.(searchValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchValue, filteredOptions]);

  useEffect(() => {
    resetSelectedValues();
    if (multiple) {
      if (props.values.includes('all')) {
        setAllSelected(true);
        onChange(['all']);
      }
    }
  }, []);

  useEffect(() => {
    resetSelectedValues();
  }, [...(multiple ? [props.values] : [props.value]), options]);

  const resetSelectedValues = () => {
    if (multiple) {
      const valuesObj = props.values.reduce((acc: SelectItem[], v) => {
        const val = options.find((o) => o.value === v);
        if (val) acc.push(val);
        return acc;
      }, []);
      setSelectedValueObj(valuesObj as SelectItem[]);
    } else {
      const valueObj = options.find((o) => o.value === props.value);
      setSelectedValueObj(valueObj ? [valueObj] : []);
    }
  };

  const handleRemoveItem = (value: string) => {
    if (multiple) {
      const newValues = removeItem(value);
      onChange(newValues);
    }
  };

  const removeItem = (value: string) => {
    if (multiple) {
      return props.values.filter((v) => v !== value);
    }
    return [];
  };

  const handleItemSelect = (item: SelectItem) => {
    if (multiple) {
      handleMultiSelect(item.value);
    } else {
      handleSingleSelect(item.value);
    }
  };

  const handleMultiSelect = (value: string) => {
    if (multiple) {
      if (value === 'all') {
        setAllSelected(true);
        onChange(['all']);
        setShowDropdown(false);
        return;
      }

      let localValues = [...props.values];
      let returnVal: string[] = [];
      if (isAllSelected) {
        setAllSelected(false);
        localValues = removeItem('all');
      }
      if (localValues.includes(value)) {
        returnVal = removeItem(value);
      } else {
        returnVal = [...localValues, value];
      }

      onChange(returnVal);
    }
  };

  const handleSingleSelect = (value: string) => {
    if (multiple) {
      setAllSelected(value === 'all');
      onChange([value]);
    } else onChange(value);

    setShowDropdown(false);
  };

  const checkToCloseDropdown = () => {
    if (options.length === 0) setShowDropdown(false);
  };

  return (
    <div className={`select__container ${className}`}>
      <Dropdown
        disabled={disabled}
        visible={showDropdown}
        placement='bottom-start'
        onClickOutside={() => setShowDropdown(false)}
        content={
          <div
            role='menu'
            className={`input-box ${small ? 'input-box__small' : ''} ${
              multiple && props.values.length > 0 ? 'has-values' : ''
            } ${showDropdown && !disabled ? 'dropdown-open' : ''}`}
            onClick={() => setShowDropdown(!showDropdown)}>
            {placeholder && (multiple ? props.values.length === 0 : !props.value) ? (
              <span className='input-box__placeholder'>{placeholder}</span>
            ) : null}

            <SelectedValues
              isAllSelected={isAllSelected}
              multiple={multiple}
              placeholder={placeholder}
              values={selectedValueObjs}
              onRemoveItem={handleRemoveItem}
            />
            <span role='button' className='input-box__arrow'>
              <ArrowDownIcon />
            </span>
          </div>
        }>
        <DropdownList>
          {dropdownPlaceholder && (
            <DropdownItem isLabel className='placeholder' onClick={checkToCloseDropdown}>
              {dropdownPlaceholder}
            </DropdownItem>
          )}

          {hasSearch ? (
            <div className='sticky top-1 z-[2] px-2'>
              <Input
                small
                type='search'
                placeholder='Search'
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
          ) : null}

          {loading ? (
            <OptionsList
              options={[{ value: 'loading', text: 'Loading...' }]}
              hideCheckbox={true}
              selectedValues={multiple ? props.values : [props.value]}
            />
          ) : (
            <OptionsList
              options={filteredOptions}
              hideCheckbox={(multiple && props.hideCheckbox) || !multiple}
              selectedValues={multiple ? props.values : [props.value]}
              onItemSelect={handleItemSelect}
              checkToCloseDropdown={checkToCloseDropdown}
            />
          )}
        </DropdownList>
      </Dropdown>
    </div>
  );
}

function OptionsList(props: OptionListProps) {
  const { options, hideCheckbox, selectedValues, onItemSelect, checkToCloseDropdown } = props;

  return options.length === 0 ? (
    <DropdownItem isLabel className='option__empty' onClick={() => checkToCloseDropdown?.()}>
      No available options
    </DropdownItem>
  ) : (
    <>
      {options.map((option) => {
        const isActive = selectedValues.includes(option.value);
        return (
          <DropdownItem
            key={option.value}
            buttonClassName={`option__item ${hideCheckbox ? 'no-checkbox' : ''} ${
              isActive ? 'is-active' : ''
            }`}
            onClick={() => onItemSelect?.(option)}>
            {!hideCheckbox && (
              <span className='option__checkbox'>
                <label className='radio__label'>
                  <input
                    className='radio radio--red radio--checkbox'
                    type='checkbox'
                    checked={isActive}
                    readOnly
                  />
                </label>
              </span>
            )}
            <span className='option__text'>{option.text}</span>
          </DropdownItem>
        );
      })}
    </>
  );
}

function SelectedValues(props: SelectedValuesProps) {
  const { values, isAllSelected, multiple, onRemoveItem } = props;

  return (
    <>
      {isAllSelected ? (
        <p className='chip__single'>All</p>
      ) : !multiple ? (
        <p className='chip__single'>{values[0]?.text}</p>
      ) : (
        values.map((option, index) => (
          <button
            type='button'
            className='chip'
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveItem(option.value);
            }}>
            {option.text}
            <span className='chip__remove'>
              <RemoveIcon />
            </span>
          </button>
        ))
      )}
    </>
  );
}
